/**
 * Hand-drawn 16×16 inline stroke icons (redesign § Design language).
 *
 * One place for every glyph the UI uses — 1.5px stroke, round caps, `currentColor` — so icons
 * inherit text color and need no library. Each accepts an optional `size` (defaults to 16).
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 16, ...rest }: IconProps): SVGProps<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...rest,
  };
}

export function PlusIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M8 3.5v9M3.5 8h9" />
    </svg>
  );
}

export function RefreshIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M13.2 8a5.2 5.2 0 1 1-1.6-3.76" />
      <path d="M13.4 2.8v3h-3" />
    </svg>
  );
}

export function GearIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="8" cy="8" r="2.1" />
      <path d="M8 1.9v1.7M8 12.4v1.7M1.9 8h1.7M12.4 8h1.7M3.7 3.7l1.2 1.2M11.1 11.1l1.2 1.2M12.3 3.7l-1.2 1.2M4.9 11.1l-1.2 1.2" />
    </svg>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M10 3.5 5.5 8 10 12.5" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6 3.5 10.5 8 6 12.5" />
    </svg>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 6.6a4 4 0 0 1 8 0c0 2.5.6 3.6 1.2 4.3.3.3.1.9-.4.9H3.2c-.5 0-.7-.6-.4-.9C3.4 10.2 4 9.1 4 6.6Z" />
      <path d="M6.6 13.8a1.6 1.6 0 0 0 2.8 0" />
    </svg>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.5 6 8 10.5 12.5 6" />
    </svg>
  );
}

export function ExternalLinkIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6.5 3.5H3.8A1.3 1.3 0 0 0 2.5 4.8v7.4a1.3 1.3 0 0 0 1.3 1.3h7.4a1.3 1.3 0 0 0 1.3-1.3V9.5" />
      <path d="M9.5 2.5h4v4M13.2 2.8 7.5 8.5" />
    </svg>
  );
}

export function CopyIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="5.6" y="5.6" width="8.2" height="8.2" rx="1.5" />
      <path d="M4.1 10.4h-.5A1.5 1.5 0 0 1 2.1 8.9V3.6a1.5 1.5 0 0 1 1.5-1.5h5.3a1.5 1.5 0 0 1 1.5 1.5v.5" />
    </svg>
  );
}

/** Two chain links — "Copy link", as distinct from ExternalLinkIcon's "open elsewhere". */
export function LinkIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6.67 8.67a3.33 3.33 0 0 0 5.03.36l2-2a3.33 3.33 0 0 0-4.71-4.71l-1.15 1.14" />
      <path d="M9.33 7.33a3.33 3.33 0 0 0-5.03-.36l-2 2a3.33 3.33 0 0 0 4.71 4.71l1.14-1.14" />
    </svg>
  );
}

export function NotionIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="2.3" y="2.3" width="11.4" height="11.4" rx="2" />
      <path d="M5.9 11V5l4.2 6V5" />
    </svg>
  );
}

/** Tick — the icon-only "Copied" confirmation. */
export function CheckIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.2 8.5 6.4 11.7 12.8 4.9" />
    </svg>
  );
}

export function InfoIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 7.4v3.4" />
      <path d="M8 5.1v.1" />
    </svg>
  );
}

export function KebabIcon(props: IconProps) {
  const { size = 16, ...rest } = props;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
      {...rest}
    >
      <circle cx="8" cy="3.2" r="1.3" />
      <circle cx="8" cy="8" r="1.3" />
      <circle cx="8" cy="12.8" r="1.3" />
    </svg>
  );
}

/** Tiny sort arrow for table headers; `dir` flips it. */
export function SortArrowIcon({ dir = 'asc', ...props }: IconProps & { dir?: 'asc' | 'desc' }) {
  return (
    <svg {...base({ size: 10, ...props })}>
      {dir === 'asc' ? <path d="M8 12.5v-9M4.5 7 8 3.5 11.5 7" /> : <path d="M8 3.5v9M4.5 9 8 12.5 11.5 9" />}
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M8 2.5v7.5M4.5 6.8 8 10.3l3.5-3.5" />
      <path d="M2.8 13.2h10.4" />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="7" cy="7" r="4.2" />
      <path d="M10.2 10.2 13.5 13.5" />
    </svg>
  );
}

/* ------------------------------------------------- view-header action glyphs */

export function FilterIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M2.4 3.4h11.2L9.3 8.5v4.3l-2.6 1.4V8.5z" />
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="2.3" y="3.4" width="11.4" height="10.3" rx="1.6" />
      <path d="M2.3 6.5h11.4M5.4 2.1v2.5M10.6 2.1v2.5" />
    </svg>
  );
}

export function ColumnsIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="2.3" y="3" width="11.4" height="10" rx="1.6" />
      <path d="M6.1 3v10M9.9 3v10" />
    </svg>
  );
}

/** Stacked cards — the global feed (`/feed`): a card with a text line, and the next one behind. */
export function FeedIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="2.3" y="2.6" width="11.4" height="6.4" rx="1.6" />
      <path d="M4.9 11.6h6.2M6.2 13.9h3.6" />
    </svg>
  );
}

/** Eye — New Post Watching (`/watch` and the Watching column): the app is *watching* an account. */
export function EyeIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M1.6 8S4.1 3.6 8 3.6 14.4 8 14.4 8 11.9 12.4 8 12.4 1.6 8 1.6 8Z" />
      <circle cx="8" cy="8" r="1.9" />
    </svg>
  );
}

/** Ticked list — the Counted-post-types popover (Gear/Filter/Columns all mean something else). */
export function ChecklistIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M2.2 4.3 3.4 5.5 5.6 3.3" />
      <path d="M2.2 10.5l1.2 1.2 2.2-2.2" />
      <path d="M7.8 4.5h6M7.8 10.7h6" />
    </svg>
  );
}

/* ------------------------------------------------------- row-thumb glyphs */

/**
 * Play triangle — the video marker on a Posts-table thumb.
 *
 * Filled rather than stroked, the one exception in this file: the glyph renders at ~8px inside a
 * 12px disc, where a 1.5px outline is most of the shape and reads as a blur.
 */
export function PlayIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5.6 3.4 12.4 8l-6.8 4.6z" fill="currentColor" />
    </svg>
  );
}

/** Page with a folded corner — the document/carousel marker on a Posts-table thumb. */
export function DocIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M9.2 2.2H4.9a1.4 1.4 0 0 0-1.4 1.4v8.8a1.4 1.4 0 0 0 1.4 1.4h6.2a1.4 1.4 0 0 0 1.4-1.4V5.4z" />
      <path d="M9.2 2.2v3.2h3.3" />
    </svg>
  );
}

/* ---------------------------------------------------------- metric glyphs */

export function ThumbsUpIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M1.9 7.3h3.2v6.5H1.9z" />
      <path d="M5.1 7.3 8.2 2.5a1.4 1.4 0 0 1 2.2 1.7L9.3 6.6h3.2a1.4 1.4 0 0 1 1.35 1.8l-1.15 4.1a1.5 1.5 0 0 1-1.45 1.1H5.1" />
    </svg>
  );
}

export function CommentIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M13.6 4.4v5a1.8 1.8 0 0 1-1.8 1.8H6.6l-3.1 2.4v-2.4h-.3A1.8 1.8 0 0 1 2.4 9.4v-5a1.8 1.8 0 0 1 1.8-1.8h7.6a1.8 1.8 0 0 1 1.8 1.8z" />
    </svg>
  );
}

export function RepostIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4.2 7.2V5.3a1.8 1.8 0 0 1 1.8-1.8h5.9" />
      <path d="M9.9 1.5 11.9 3.5 9.9 5.5" />
      <path d="M11.8 8.8v1.9a1.8 1.8 0 0 1-1.8 1.8H4.1" />
      <path d="M6.1 14.5 4.1 12.5 6.1 10.5" />
    </svg>
  );
}

export function BookmarkIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.9 3.3a1 1 0 0 1 1-1h6.2a1 1 0 0 1 1 1v10.4L8 10.6l-4.1 3.1z" />
    </svg>
  );
}

/* ------------------------------------------------------ comment generation */

/**
 * Four-point sparkle — "Generate comment" (SPEC `Comment Generation` § The action).
 *
 * A big star with a small companion, the established "this was written by a model" glyph. Stroked
 * like the rest of the set, so it sits in the card footer beside Copy and Save without shouting.
 */
export function SparkleIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6.6 1.9 7.85 5.15 11.1 6.4 7.85 7.65 6.6 10.9 5.35 7.65 2.1 6.4 5.35 5.15Z" />
      <path d="M11.9 9.7 12.5 11.3 14.1 11.9 12.5 12.5 11.9 14.1 11.3 12.5 9.7 11.9 11.3 11.3Z" />
    </svg>
  );
}

/**
 * Upward arrow — the chat composer's send button (§20.8).
 *
 * Sits alone in a 28px circle, so it is drawn a touch shorter than the 16px box to leave the disc
 * some breathing room.
 */
export function ArrowUpIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M8 12.8V3.6M4.2 7.4 8 3.6l3.8 3.8" />
    </svg>
  );
}

/* ------------------------------------------------------------ saved posts */

/**
 * The saved state of the bookmark action (`BookmarkIcon` is the unsaved one). Filled rather than
 * stroked — the second exception in this file
 * after `PlayIcon`: at 14px an outlined "on" state is indistinguishable from the "off" one.
 */
export function BookmarkFilledIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M3.9 3.3a1 1 0 0 1 1-1h6.2a1 1 0 0 1 1 1v10.4L8 10.6l-4.1 3.1z"
        fill="currentColor"
      />
    </svg>
  );
}

/* --------------------------------------------------- content performance */

/**
 * Bar-chart glyph — the homescreen's Content button and the screen's own chrome (SPEC
 * `Content Performance` § Navigation & chrome). Three columns on a baseline: the one shape that
 * says "reported numbers over time" without repeating the Feed or Watch glyphs.
 */
export function ChartIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M2.5 13.5h11" />
      <path d="M4.6 13.5V8.4M8 13.5V3.6M11.4 13.5V6.2" />
    </svg>
  );
}

/** Upload tray — the Import action of the Content screen (the mirror of `DownloadIcon`). */
export function UploadIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M8 10.4V2.6M5 5.6 8 2.6l3 3" />
      <path d="M2.8 10.2v2.1a1 1 0 0 0 1 1h8.4a1 1 0 0 0 1-1v-2.1" />
    </svg>
  );
}
